import React, { useState } from 'react';
import './PublicHome.css';
import ScreenshotCarousel from './ScreenshotCarousel';
import TestimonialCarousel from './TestimonialCarousel';
import Footer from './Footer';
import HeroSection from './HeroSection';
import Seo from './Seo';
import { screenshots } from '../config/screenshots';
import { SITE_URL, SITE_NAME } from '../config/seo';

const testimonials = [
  {
    quote: 'MemBrowse is exactly what we needed - a really clean and convenient tool, thanks!',
    name: 'Sergei Gavrilov',
    title: 'Flipper Devices',
    logo: '/flipper-logo.png',
  },
  {
    quote: 'MemBrowse is super useful for analyzing size, which we are trying to do better with TinyUSB recently.',
    name: 'Ha Thach',
    title: 'TinyUSB',
    logo: '/tinyusb-logo.svg',
  },
  {
    quote: "Loving MemBrowse so far. We've been trying to hand-roll solutions to this for far too long, and they just kept breaking. A few devs have already started submitting PRs to claw back flash space.",
    name: 'Austin',
    title: 'Meshtastic',
    logo: '/meshtastic-logo.svg',
  },
  {
    quote: 'I find MemBrowse really useful for firmware devs, especially if development is using CI.',
    name: 'Dmitry K.',
    title: 'SuperTinyKernel',
    logo: '/supertinykernel-logo.png',
  },
  {
    quote: 'MemBrowse helps developers better understand and optimize memory usage, making embedded development even more efficient.',
    name: 'Max Song',
    title: 'RT-Thread',
    logo: '/rt-thread-logo.png',
  },
  {
    quote: 'Now we will spot any increase automatically thanks to MemBrowse integration on our GitHub.',
    name: 'Alan C. Assis',
    title: 'Apache NuttX',
    logo: '/nuttx-logo.png',
  },
  {
    quote: "Fixes many of the frustrations I've had at times trying to hunt down linker padding feng-shui ghosts.",
    name: 'Anson Mansfield',
    title: 'MicroPython',
    logo: '/micropython-logo.png',
  },
];

const FAQ_ITEMS = [
  {
    question: 'What architectures and toolchains are supported?',
    answerText: 'MemBrowse works with any architecture that produces ELF binaries and memory maps. ' +
      'We support ARM, RISC-V, x86, and more. Popular toolchains like GCC, Clang, IAR, and Keil are all compatible.',
  },
  {
    question: 'How is my data secured?',
    answerText: 'We store only memory analysis results, never your source code or binaries. Your ELF ' +
      'files are analyzed in your CI environment; only JSON reports containing symbol names, ' +
      'addresses, and sizes are uploaded. All data is encrypted in transit (TLS 1.3) and at rest, ' +
      'with row-level security ensuring you can only access your own projects. For maximum control, ' +
      'enterprise on-premises deployment is available where your data never leaves your infrastructure.',
  },
  {
    question: 'Can I deploy MemBrowse on-premises?',
    answerText: 'Yes! Enterprise customers can deploy the entire MemBrowse platform on their own infrastructure ' +
      'for complete data control. The on-premises deployment includes the web dashboard, API, and database, ' +
      'all running within your network. Request a demo to discuss enterprise deployment options.',
    answer: (
      <>
        Yes! Enterprise customers can deploy the entire MemBrowse platform on their own infrastructure
        for complete data control. The on-premises deployment includes the web dashboard, API, and database,
        all running within your network.{' '}
        <a href="/demo" onClick={(e) => e.stopPropagation()}>Request a demo</a> to discuss enterprise deployment options.
      </>
    ),
  },
  {
    question: 'How difficult is integration?',
    answerText: 'Integration takes minutes, not hours. Add a few lines to your CI configuration, ' +
      'run our open source tool after your build, and you’re done. Check out our documentation ' +
      'for step-by-step instructions.',
    answer: (
      <>
        Integration takes minutes, not hours. Add a few lines to your CI configuration,
        run our open source tool after your build, and you&apos;re done. Check out our{' '}
        <a href="https://docs.membrowse.com" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>documentation</a> for step-by-step instructions.
      </>
    ),
  },
  {
    question: 'What CI/CD platforms do you support?',
    answerText: 'GitHub Actions and GitLab CI are natively supported with ready-to-use ' +
      'workflows. For other CI platforms (Jenkins, CircleCI, Travis CI, Azure DevOps), ' +
      'you can use the MemBrowse CLI directly. It works with any system that can execute shell commands.',
  },
  {
    question: 'What’s included in the Open Source tier?',
    answerText: 'The Open Source tier is completely free and includes unlimited usage for public ' +
      'repositories: unlimited tracked targets, unlimited users, and unlimited memory reports. ' +
      'Private repositories need a paid plan. See our pricing page for details.',
    answer: (
      <>
        The Open Source tier is completely free and includes unlimited usage for public
        repositories: unlimited tracked targets, unlimited users, and unlimited memory reports.
        Private repositories need a paid plan. See our{' '}
        <a href="/pricing" onClick={(e) => e.stopPropagation()}>pricing page</a> for details.
      </>
    ),
  },
];

const HOME_JSON_LD = [
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/membrowse-logo.svg`,
    sameAs: ['https://github.com/membrowse'],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: SITE_NAME,
    url: SITE_URL,
    applicationCategory: 'DeveloperApplication',
    operatingSystem: 'Web',
    description: 'Firmware memory footprint tracking platform. Analyzes ELF binaries and linker scripts in CI to track RAM and Flash usage on every commit.',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      description: 'Free Open Source tier with unlimited usage for public repositories.',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ_ITEMS.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answerText },
    })),
  },
];

const PublicHome = () => {
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [videoStarted, setVideoStarted] = useState(false);

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  return (
    <div className="public-home">
      <Seo
        title="MemBrowse – Firmware Memory Footprint Tracking in CI"
        description="Track RAM and Flash usage on every commit. MemBrowse analyzes ELF binaries in CI, catches firmware size regressions early, and enforces memory budgets before the linker breaks."
        path="/"
        jsonLd={HOME_JSON_LD}
      />

      {/* Hero Section */}
      <HeroSection
        title="Discover firmware bloat before it blocks your release."
        subtitle="Continuous tracking of memory footprint on every commit, so you stop discovering size issues only when the linker breaks."
        variant="split"
        ctaButtonText="See it in action with real data"
        ctaDescription="Explore memory trends, symbol diffs, and build history from live CI runs."
        ctaLink="/showcase"
        ctaButtonLabel="Projects Showcase"
      />

      {/* Ecosystem / Used-by Section */}
      <section className="ecosystem-section">
        <div className="section-container">
          <p className="ecosystem-heading">Trusted by</p>
          <div className="ecosystem-logos">
            <img src="/rt-thread-logo.png" alt="RT-Thread" className="ecosystem-logo ecosystem-logo--rtthread" />
            <img src="/nuttx-logo.png" alt="Apache NuttX" className="ecosystem-logo ecosystem-logo--nuttx" />
            <img src="/adafruit-logo.svg" alt="Adafruit" className="ecosystem-logo ecosystem-logo--adafruit" />
            <img src="/meshtastic-logo.svg" alt="Meshtastic" className="ecosystem-logo ecosystem-logo--meshtastic" />
            <img src="/zephyr-logo.svg" alt="Zephyr RTOS" className="ecosystem-logo ecosystem-logo--zephyr" />
            <img src="/wolfssl-logo.png" alt="wolfSSL" className="ecosystem-logo ecosystem-logo--wolfssl" />
            <img src="/tinyusb-logo.svg" alt="TinyUSB" className="ecosystem-logo ecosystem-logo--tinyusb" />
            <img src="/flipper-logo.png" alt="Flipper Devices" className="ecosystem-logo ecosystem-logo--flipper" />
            <img src="/cmrx-logo.png" alt="CMRX" className="ecosystem-logo ecosystem-logo--cmrx" />
            <img src="/supertinykernel-logo.png" alt="SuperTinyKernel" className="ecosystem-logo ecosystem-logo--stk" />
          </div>
        </div>
      </section>

      {/* Explainer Video Section */}
      <section className="video-section">
        <div className="section-container">
          {videoStarted ? (
            <video
              className="explainer-video"
              controls
              autoPlay
              playsInline
              poster="/video/poster.jpg"
            >
              <source src="/video/membrowse-explainer.mp4" type="video/mp4" />
              <track
                kind="captions"
                src="/video/membrowse-explainer.vtt"
                srcLang="en"
                label="English"
              />
            </video>
          ) : (
            // The poster has its play button drawn in, so the whole poster starts the video
            <button
              type="button"
              className="explainer-video explainer-video-poster"
              onClick={() => setVideoStarted(true)}
              aria-label="Play video: Watch how it works"
            >
              <img src="/video/poster.jpg" alt="" />
            </button>
          )}
        </div>
      </section>

      {/* Screenshots Section */}
      <section className="screenshots-section">
        <div className="section-container">
          <ScreenshotCarousel screenshots={screenshots} />
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="testimonial-section">
        <div className="section-container">
          <TestimonialCarousel testimonials={testimonials} />
        </div>
      </section>

      {/* Target Audience Section */}
      <section className="audience-section">
        <div className="section-container">
          <h2 className="section-title">Built for Modern Firmware Teams</h2>
          <p className="section-subtitle">
            Whether you&apos;re shipping IoT devices, automotive systems, or consumer electronics,
            MemBrowse helps you track memory, enforce budgets, and ship confidently.
          </p>
          <div className="audience-grid">
            <div className="audience-card">
              <div className="audience-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="2" y="3" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
                  <path d="M8 21h8M12 17v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <h3 className="audience-title">Firmware Developers</h3>
              <p className="audience-description">
                Track code size per commit, spot regressions early, and fix issues before they block release.
              </p>
            </div>

            <div className="audience-card">
              <div className="audience-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                  <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <h3 className="audience-title">Embedded Teams</h3>
              <p className="audience-description">
                Collaborate on memory optimization with shared dashboards and commit history for every target.
              </p>
            </div>

            <div className="audience-card">
              <div className="audience-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3 className="audience-title">Team Leads &amp; Managers</h3>
              <p className="audience-description">
                Monitor memory budgets across projects, set thresholds, and keep releases on schedule.
              </p>
            </div>

            <div className="audience-card">
              <div className="audience-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3 className="audience-title">Cross-Platform Projects</h3>
              <p className="audience-description">
                Compare memory footprints across boards and builds to ensure every configuration fits.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="problem-section">
        <div className="section-container">
          <h2 className="section-title">The Binary Bloat Problem</h2>
          <div className="problem-grid">
            <div className="problem-card">
              <div className="problem-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 3v18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M7 12l4-4 4 4 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3 className="problem-title">No Historical Context</h3>
              <p className="problem-description">
                Without tracking memory footprint trends over time, it's impossible to identify gradual binary growth patterns.
              </p>
            </div>

            <div className="problem-card">
              <div className="problem-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 9v4M12 17h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <h3 className="problem-title">Invisible Regressions</h3>
              <p className="problem-description">
                Code bloat sneaks into your codebase without warning, discovered too late in the development cycle.
              </p>
            </div>

            <div className="problem-card">
              <div className="problem-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 2v6h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M10 13l4 4M14 13l-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3 className="problem-title">Plan&nbsp;vs.&nbsp;Implementation</h3>
              <p className="problem-description">
                Memory budgets are set during planning, but there&apos;s no easy way to verify if your implementation actually matches the intended allocation.
              </p>
            </div>

            <div className="problem-card">
              <div className="problem-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <h3 className="problem-title">Manual Analysis</h3>
              <p className="problem-description">
                Hours spent manually analyzing ELF files and memory maps to understand where memory is being used.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="solution-section">
        <div className="section-container">
          <h2 className="section-title">Automated Memory Intelligence</h2>
          <div className="solution-grid">
            <div className="solution-card">
              <div className="solution-icon">
                <svg width="56" height="56" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 2v6h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 13H8M16 17H8M10 9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3 className="solution-title">Memory Report Generation</h3>
              <p className="solution-description">
                Generate detailed memory reports from ELF binaries and linker scripts using our GitHub Action or CLI tool, supporting any architecture.
              </p>
            </div>

            <div className="solution-card">
              <div className="solution-icon">
                <svg width="56" height="56" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
                  <rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
                  <rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
                  <rect x="14" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <h3 className="solution-title">Visual Insights</h3>
              <p className="solution-description">
                Code size trends, memory layouts, and visual binary comparisons make complex data easy to understand.
              </p>
            </div>

            <div className="solution-card">
              <div className="solution-icon">
                <svg width="56" height="56" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0115.5-8.5l4 4M22 12.5a10 10 0 01-15.5 8.5l-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3 className="solution-title">PR Gate Checks</h3>
              <p className="solution-description">
                Configure your pull requests to fail when changes exceed memory budgets, alerting developers early and preventing late stage issues.
              </p>
            </div>

            <div className="solution-card">
              <div className="solution-icon">
                <svg width="56" height="56" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M16 2v4M8 2v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <h3 className="solution-title">CI/CD Integration</h3>
              <p className="solution-description">
                Integrates with any CI/CD platform easily. Native support for GitHub Actions and CLI support for other platforms.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section">
        <div className="section-container">
          <h2 className="section-title">How It Works</h2>
          <div className="steps-container">
            <div className="step-card">
              <div className="step-number">1</div>
              <h3 className="step-title">Integrate with CI/CD</h3>
              <p className="step-description">
                Add MemBrowse to your CI/CD pipeline - works with GitHub Actions, GitLab CI, Jenkins, and any CI system.
              </p>
            </div>

            <div className="step-arrow">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>

            <div className="step-card">
              <div className="step-number">2</div>
              <h3 className="step-title">Generate Report</h3>
              <p className="step-description">
                Your CI builds the firmware and generates a memory report using the open source MemBrowse CLI from the membrowse-action repository.
              </p>
            </div>

            <div className="step-arrow">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>

            <div className="step-card">
              <div className="step-number">3</div>
              <h3 className="step-title">Upload Reports</h3>
              <p className="step-description">
                Memory reports are sent to MemBrowse and fail the CI job if usage exceeds your defined budgets.
              </p>
            </div>

            <div className="step-arrow">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>

            <div className="step-card">
              <div className="step-number">4</div>
              <h3 className="step-title">Visualize &amp; Optimize</h3>
              <p className="step-description">
                Explore your memory data in interactive dashboards, compare any two builds, and track changes over time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="section-container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="home-faq-list">
            {FAQ_ITEMS.map((item, index) => (
              <div
                key={item.question}
                className={`home-faq-item ${openFaqIndex === index ? 'open' : ''}`}
                onClick={() => toggleFaq(index)}
              >
                <div className="home-faq-question-button">
                  <h3 className="home-faq-question">{item.question}</h3>
                  <svg className="home-faq-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="home-faq-answer-wrapper">
                  <p className="home-faq-answer">{item.answer || item.answerText}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="partners-section">
        <div className="partners-content">
          <div className="partner-badge">
            <span className="partner-text">Supported by</span>
            <img
              src="/googlecloud.png"
              alt="Google Cloud for Startups"
              className="partner-logo"
            />
          </div>
          <div className="partner-badge">
            <span className="partner-text">Technology Partner</span>
            <img
              src="/github-logo.png"
              alt="GitHub Technology Partner"
              className="partner-logo github-logo"
            />
          </div>
          <div className="partner-badge">
            <span className="partner-text">Technology Partner</span>
            <img
              src="/gitlab-logo.png"
              alt="GitLab Technology Partner"
              className="partner-logo gitlab-logo"
            />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PublicHome;
